// SPDX-License-Identifier: BUSL-1.1

/**
 * Moderation Inbox — governance review queue for tool confirmation requests.
 *
 * Displays pending, approved, and rejected moderation items with approve/reject
 * actions. Supports auto-refresh polling and status filtering.
 *
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Inbox, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DashboardTopbar } from '@/components/dashboard-topbar';
import { ModerationItemCard } from '@/components/moderation-item-card';
import { InboxSkeleton } from '@/components/skeletons';
import { apiFetch } from '@/lib/api-client';
import { showSuccess, showApiError } from '@/lib/toast';
import type { ModerationItem, ModerationStatus } from '@/types/api';

/* ── Constants ─────────────────────────────────────── */

type StatusFilter = ModerationStatus | 'all';

const TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Escalated', value: 'escalated' },
];

/* ── Inbox page ────────────────────────────────────── */

export function InboxPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [autoRefresh, setAutoRefresh] = useState(true);

  /* ── Fetch governed approval items ── */
  // GET /api/governance/approval-requests returns { items, total, status }
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['governance-approval-requests', statusFilter],
    queryFn: () => {
      const qs = statusFilter === 'all' ? '?status=all' : `?status=${statusFilter}`;
      return apiFetch<{ items: ModerationItem[]; total: number }>(
        `/governance/approval-requests${qs}`,
      );
    },
    refetchInterval: autoRefresh ? 5000 : false,
  });
  const items = data?.items;

  /* ── Resolve mutation ── */
  const resolve = useMutation({
    mutationFn: ({
      id,
      result,
      denyMessage,
    }: {
      id: string;
      result: 'approved' | 'rejected';
      denyMessage?: string;
    }) =>
      apiFetch(`/governance/approval-requests/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ result, denyMessage }),
      }),
    onSuccess: (_data, variables) => {
      showSuccess(variables.result === 'approved' ? 'Approval granted' : 'Approval rejected');
      void queryClient.invalidateQueries({ queryKey: ['governance-approval-requests'] });
    },
    onError: showApiError,
  });

  const pendingCount = items?.filter((i) => i.status === 'pending').length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <DashboardTopbar
        title="Approval Queue"
        subtitle="Governed Session Reviews"
        onRefresh={() => void refetch()}
        isLive={autoRefresh}
        actions={
          <Button
            variant={autoRefresh ? 'secondary' : 'outline'}
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className="size-3" />
            {autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
          </Button>
        }
      />

      {/* Pending count */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-2 px-1">
          <Badge variant="destructive">{pendingCount} pending</Badge>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 px-1">
        {TABS.map((tab) => (
          <Button
            key={tab.value}
            variant={statusFilter === tab.value ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <InboxSkeleton />
      ) : !items?.length ? (
        <EmptyState filter={statusFilter} />
      ) : (
        <div className="space-y-3 px-1">
          {items.map((item) => (
            <ModerationItemCard
              key={item.id}
              item={item}
              isResolving={resolve.isPending}
              onApprove={(id) => resolve.mutate({ id, result: 'approved' })}
              onReject={(id, denyMessage) =>
                resolve.mutate({ id, result: 'rejected', denyMessage })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Empty state ───────────────────────────────────── */

function EmptyState({ filter }: { filter: StatusFilter }) {
  const message =
    filter === 'pending'
      ? 'No pending approvals'
      : filter === 'all'
        ? 'No approval requests'
        : `No ${filter} approvals`;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 text-neutral-500">
      <Inbox className="size-12 text-neutral-600" />
      <h2 className="text-lg font-semibold text-neutral-300">{message}</h2>
      <p className="max-w-sm text-center text-sm">
        When agent tool calls require human review, they will appear here for approval or rejection.
      </p>
    </div>
  );
}
